import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { PageContainer, PageHeader, Card, Button, Badge, AlertBanner, Muted } from "../ui";

// mPower's nightly report emails, one row per business date: did Daily Sales,
// the Tender Report, the Tax Report and (Mondays) Customer Points arrive and
// import? Paula 10/13: "an import log page showing each report and date with
// imported / missing / error."

type Status = "imported" | "skipped" | "missing" | "error";
interface LogRow { report_type: string; business_date: string; status: Status; rows_imported: number; rows_skipped: number; detail: string | null; attachment_name: string | null; updated_at: string }

const TYPES: { key: string; label: string }[] = [
  { key: "daily_sales", label: "Daily Sales" },
  { key: "tender", label: "Tender Report" },
  { key: "tax", label: "Tax Report" },
  { key: "customer_points", label: "Customer Points (weekly)" },
  { key: "photo", label: "Photos emailed (invoice / budget sheet)" },
];

function variantFor(s: Status | undefined): "green" | "danger" | "gold" | "default" {
  if (s === "imported" || s === "skipped") return "green";
  if (s === "error") return "danger";
  if (s === "missing") return "gold";
  return "default";
}

export default function ReportImportsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<LogRow[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [lastRun, setLastRun] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await api.get("/mpower-feed/log", { params: { days: 60 } });
    setRows(res.data.rows || []); setDates(res.data.dates || []); setConfigured(Boolean(res.data.configured)); setLastRun(res.data.lastRun || null);
  }, []);
  useEffect(() => { load().catch(() => setMessage("Could not load the import log.")); }, [load]);

  const runNow = async () => {
    setRunning(true); setMessage(null);
    try {
      const res = await api.post("/mpower-feed/run", {});
      const s = res.data;
      setMessage(`Checked ${s.messages} emails: ${s.imported.length} imported, ${s.skipped.length} already in, ${s.errors.length} errors.`);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setMessage(err.response?.data?.error || "Run failed.");
    } finally { setRunning(false); }
  };

  const cell = (date: string, type: string) => rows.find((r) => r.business_date === date && r.report_type === type);
  const missingCount = rows.filter((r) => r.status === "missing" || r.status === "error").length;

  return (
    <PageContainer>
      <PageHeader
        title="Report Imports"
        subtitle={configured === false ? "Gmail is not connected yet" : lastRun ? `Last import ${new Date(lastRun).toLocaleString("en-US", { timeZone: "America/New_York" })} ET` : "No imports yet"}
        action={user?.role === "owner" ? <Button onClick={runNow} disabled={running || configured === false}>{running ? "Reading mailbox..." : "Run now"}</Button> : undefined}
      />
      {configured === false && (
        <AlertBanner variant="gold">
          Karen cannot read the store Gmail until the one-time authorisation is done: <code>npm run gmail-auth</code> on the Mac, then the GMAIL_* lines in the server .env. The 6:00 AM run starts the morning after.
        </AlertBanner>
      )}
      {message && <AlertBanner variant="blue">{message}</AlertBanner>}
      {missingCount > 0 && <AlertBanner variant="gold">{missingCount} report{missingCount !== 1 ? "s" : ""} missing or in error in the last 60 days. Missing nightly reports are texted to the owner at 6:45 AM.</AlertBanner>}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: "var(--rbw-muted)" }}>
                <th className="text-left py-2 pr-4">Business date</th>
                {TYPES.map((t) => <th key={t.key} className="text-left py-2 pr-4">{t.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {dates.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center"><Muted>Nothing logged yet. The first run reads every report email since October 10, 2026.</Muted></td></tr>
              )}
              {dates.map((d) => (
                <tr key={d} style={{ borderTop: "1px solid var(--rbw-border)" }}>
                  <td className="py-2 pr-4 font-medium" style={{ color: "var(--rbw-navy)" }}>{new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</td>
                  {TYPES.map((t) => {
                    const r = cell(d, t.key);
                    const key = `${d}|${t.key}`;
                    if (!r) return <td key={t.key} className="py-2 pr-4"><Muted>{t.key === "customer_points" ? "" : "--"}</Muted></td>;
                    return (
                      <td key={t.key} className="py-2 pr-4">
                        <button type="button" onClick={() => setOpen(open === key ? null : key)} className="text-left">
                          <Badge variant={variantFor(r.status)}>{r.status}{r.status === "imported" && r.rows_imported ? ` ${r.rows_imported.toLocaleString()}` : ""}</Badge>
                        </button>
                        {open === key && (
                          <div className="mt-1 text-xs whitespace-pre-wrap rounded p-2" style={{ background: "var(--rbw-bg)", color: "var(--rbw-gray-dark)", maxWidth: 520 }}>
                            {r.attachment_name && <div><strong>{r.attachment_name}</strong></div>}
                            {r.detail || "No detail recorded."}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="mt-3 text-xs" style={{ color: "var(--rbw-muted)" }}>
        Daily Sales lines are keyed on mPower's DocumentDetailID, so a re-run never double-counts a day. CHECK tenders are recorded as card (Helcim).
      </div>
    </PageContainer>
  );
}
